import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Button, SpaceBetween, Box, Modal, Link } from '@cloudscape-design/components';
import { AgGridReact } from 'ag-grid-react';
import { initializeAgGridLicense } from 'src/utils/License';
import {
  GridApi,
  CellValueChangedEvent,
  GridReadyEvent,
  FilterChangedEvent,
  FilterModel,
  ColumnState,
  ColDef,
} from 'ag-grid-community';
import { CustomCellPopoverRenderer } from 'src/common/CustomCellPopoverRenderer';
import {
  handleSaveChanges,
  processControlData,
  createAutoPopulateConfigs,
  handleAutoPopulate,
  getUserPreferences,
  CustomLoadingOverlay,
  getNormalizedGridData,
  extractTrackableGridData,
} from 'src/utils/ControlSummaryUtils';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import {
  ControlSummaryMetadata,
  RecordAttributes,
  UserViewPreferences,
} from '@amzn/controllershiphub-typescript-client';
import { recordGridLoadMetrics } from 'src/provider/RumProvider';
import { createColumnDefs, createGridOptions } from 'src/utils/GridUtils';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { useUserAlias } from 'src/common/hooks/useUserAlias';
import {
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  FiltersToolPanelModule,
  SetFilterModule,
  MultiFilterModule,
  SideBarModule,
  SideBarDef,
  CellSelectionModule,
  ClipboardModule,
} from 'ag-grid-enterprise';
import { useLocation } from 'react-router-dom';
import { DataSetDetailsModal } from 'src/components/modals/DatasetDetailsModal';
const DEFAULT_VIEW = 'Default View' as const;

initializeAgGridLicense();
ModuleRegistry.registerModules([
  AllCommunityModule,
  MultiFilterModule,
  SetFilterModule,
  FiltersToolPanelModule,
  SideBarModule,
  ColumnMenuModule,
  ContextMenuModule,
  ColumnsToolPanelModule,
  CellSelectionModule,
  ClipboardModule,
]);

interface TableUtilProps {
  controlData: RecordAttributes[][];
  controlAttributesData: ControlSummaryMetadata | null;
  isLoading: boolean;
  controlId: string;
  onFilterChange?: (filters: FilterModel) => void;
  dataFetchStartTime: number;
  onSelectionChanged?: (selectedRows: any[]) => void;
  additionalDataEnabled?: boolean;
  dataType?: 'GL' | 'PO' | 'DEFAULT';
  onDataRefresh?: () => void;
}

export interface GridRef {
  exportDataAsCsv: () => void;
  saveChanges: () => Promise<void>;
  getColumnState: () => any[];
  resetFilters: () => void;
  refreshData: (data: RecordAttributes[][]) => void;
  applyColumnState: (state: any[]) => void;
  api?: any;
  getInitialColumnState?: () => ColumnState[];
}

/**
 * ControlSummaryPageAGGrid
 *
 * This component renders a data grid using the AG Grid library to display and manage control summary data.
 * It provides features like filtering, sorting, editing, and saving changes to the control data.
 *
 * @param {Object} props - The component props
 * @param {RecordAttributes[][]} props.controlData - The control data to be displayed in the grid
 * @param {ControlAttributesMetadata | null} props.controlAttributesData - Metadata for the control attributes
 * @param {boolean} props.isLoading - Indicates whether the data is currently being loaded
 * @param {string} props.controlId - The unique identifier for the control
 */
export const ControlSummaryPageAGGrid = forwardRef<GridRef, TableUtilProps>(
  (
    {
      controlData,
      controlAttributesData,
      isLoading,
      controlId,
      onFilterChange,
      dataFetchStartTime,
      onSelectionChanged,
      additionalDataEnabled = false,
      dataType = 'DEFAULT',
      onDataRefresh,
    },
    ref
  ) => {
    const [rowData, setRowData] = useState<Record<string, any>[]>([]);
    const [editedCells, setEditedCells] = useState<Set<string>>(new Set());
    const [activeFilters, setActiveFilters] = useState<FilterModel>({});
    const [quickFilterText, setQuickFilterText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState<Record<string, any> | null>(null);
    const [selectedView, setSelectedView] = useState<string>(DEFAULT_VIEW);
    const gridApiRef = useRef<GridApi | null>(null);
    const { alias } = useUserAlias();
    const defaultFiltersAppliedRef = useRef(false);
    const [filterViews, setFilterViews] = useState<Record<string, UserViewPreferences>>({});
    const [isLoadingFilterViews, setIsLoadingFilterViews] = useState(false);
    const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
    const location = useLocation();
    const [metricsRecorded, setMetricsRecorded] = useState(false);
    const loadStartTime = useRef<number>(0);
    const [columnState, setColumnState] = useState<Record<string, boolean>>({});
    const [filteredRowCount, setFilteredRowCount] = useState<number>(0);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);

    const initialColumnStateRef = useRef<ColumnState[]>([]);

    // Define row selection configuration for AG Grid
    const rowSelection = useMemo(
      () => ({
        mode: 'multiRow' as const,
      }),
      []
    );

    // Handle selection changes in the grid
    const handleSelectionChanged = useCallback(() => {
      if (gridApiRef.current) {
        const selectedNodes = gridApiRef.current.getSelectedNodes();
        const selectedData = selectedNodes.map((node) => node.data);
        setSelectedRows(selectedData);
        if (onSelectionChanged) {
          onSelectionChanged(selectedData);
        }
      }
    }, [onSelectionChanged]);

    useEffect(() => {
      if (controlAttributesData?.attributesMetadata) {
        const initialState = controlAttributesData.attributesMetadata.reduce(
          (acc, attr) => {
            if (attr.name) {
              acc[attr.name] = attr.isVisibleByDefault !== false; // Default to true if not specified
            }
            return acc;
          },
          {} as Record<string, boolean>
        );
        setColumnState(initialState);
      }
    }, [controlAttributesData]);

    // Custom cell renderer component for DataSetKey column
    const DataSetKeyRenderer = (props: any) => {
      const value = props.value || 'N/A';
      return (
        <Link
          variant="primary"
          href="#"
          onFollow={(e) => {
            e.preventDefault();
            setSelectedRowData(props.data);
            setIsModalVisible(true);
          }}
        >
          {value}
        </Link>
      );
    };

    const columnDefs = useMemo(() => {
      const cols = createColumnDefs(
        controlAttributesData,
        editedCells,
        CustomCellPopoverRenderer,
        columnState
      );
      const linkFieldNames = controlAttributesData?.attributesMetadata
        ?.filter((attributeMetadata) => attributeMetadata.dataType === 'java.lang.Link')
        ?.map((attributeMetadata) => attributeMetadata.name);
      return cols.map((col) => {
        if (linkFieldNames?.includes(col.field) && controlAttributesData?.enableDetailedView) {
          return { ...col, cellRenderer: DataSetKeyRenderer };
        }
        return col;
      });
    }, [controlAttributesData, editedCells, columnState]);

    // Handle unsaved changes when navigating away
    useEffect(() => {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (editedCells.size > 0) {
          e.preventDefault();
          e.returnValue = '';
        }
      };
      const handlePopState = (e: PopStateEvent) => {
        if (editedCells.size > 0) {
          e.preventDefault();
          setShowUnsavedChangesModal(true);
          setPendingNavigation(() => () => {
            window.history.pushState(null, '', e.state?.url || '/');
          });
        }
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('popstate', handlePopState);
      };
    }, [editedCells]);

    useEffect(() => {
      if (!isLoading && rowData.length > 0 && !metricsRecorded && dataFetchStartTime > 0) {
        const endTime = performance.now();
        const totalLoadTime = Math.round(endTime - dataFetchStartTime);

        // Record grid load metrics using the dedicated RUM function
        recordGridLoadMetrics({
          totalLoadTimeMs: totalLoadTime,
          gridLoadTimeMs: totalLoadTime,
          rowCount: rowData.length,
          columnCount: columnDefs.length,
          hasFilters: Object.keys(activeFilters).length > 0,
          quickFilterActive: !!quickFilterText,
          controlId,
          selectedView,
          pageUrl: location.pathname,
          timeToFirstByte: totalLoadTime,
        });

        setMetricsRecorded(true);
      }
    }, [
      isLoading,
      rowData,
      metricsRecorded,
      dataFetchStartTime,
      columnDefs.length,
      activeFilters,
      quickFilterText,
      controlId,
      selectedView,
      location.pathname,
    ]);

    const handleUnsavedChangesConfirm = () => {
      setShowUnsavedChangesModal(false);
      if (pendingNavigation) {
        pendingNavigation();
        setPendingNavigation(null);
      }
    };
    useEffect(() => {
      return () => {
        setMetricsRecorded(false);
      };
    }, [controlId]);

    const handleUnsavedChangesCancel = () => {
      setShowUnsavedChangesModal(false);
      setPendingNavigation(null);
      // Push the current location back to the history to prevent navigation
      window.history.pushState(null, '', location.pathname);
    };

    /**
     * Initialize and load filter grid views in sync with filter panel filters
     */
    useEffect(() => {
      const fetchAndCombineFilters = async () => {
        setIsLoadingFilterViews(true);
        try {
          const response = await getUserPreferences(controlId);
          let combinedViews: Record<string, UserViewPreferences> = response.views || {};

          try {
            if (gridApiRef.current && !defaultFiltersAppliedRef.current) {
              defaultFiltersAppliedRef.current = true;
            }
          } catch (error) {
            console.error('Error parsing default filters:', error);
          }

          setFilterViews(combinedViews);
          setSelectedView(DEFAULT_VIEW);
        } catch (error) {
          console.error('Error loading filter views:', error);
        } finally {
          setIsLoadingFilterViews(false);
        }
      };
      fetchAndCombineFilters();
    }, [controlId, controlAttributesData]);

    // Configure Default columns
    const defaultColDef = useMemo(
      () => ({
        sortable: true,
        filter: 'agMultiColumnFilter',
        width: undefined,
        filterParams: {
          filters: [
            {
              filter: 'agTextColumnFilter',
              filterParams: {
                filterOptions: [
                  'contains',
                  'notContains',
                  'equals',
                  'notEqual',
                  'startsWith',
                  'endsWith',
                ],
                defaultOption: 'contains',
                maxNumConditions: 1,
              },
            },
            {
              filter: 'agSetColumnFilter',
            },
          ],
        },
      }),
      []
    );

    const autoPopulateConfigs = useMemo(() => createAutoPopulateConfigs(), []);

    const [originalValues, setOriginalValues] = useState<Record<string, any>>({});

    // When grid data is loaded, store the original values
    useEffect(() => {
      const processed = processControlData(controlData);
      setRowData(processed);

      if (gridApiRef.current) {
        gridApiRef.current.setGridOption('rowData', processed);
        gridApiRef.current.refreshCells({ force: true });
        gridApiRef.current.redrawRows();
      }

      const referenceValues = extractTrackableGridData(getNormalizedGridData(processed));
      setOriginalValues(referenceValues);
    }, [controlData]);

    // Modified onCellValueChanged handler
    const onCellValueChanged = useCallback(
      (event: CellValueChangedEvent) => {
        if (!event.node) return;

        const rowId = event.node.id;
        const colId = event.column.getColId();
        const cellId = `${rowId}-${colId}`;

        // Get the original and new values
        const originalValue = originalValues[cellId];
        const newValue = event.newValue;

        // Determine if the current column is a checkbox type column
        const isCheckboxColumn = event.column.getColDef().cellRenderer === 'agCheckboxCellRenderer';

        // Compare original and new values to detect changes
        let areValuesEqual;
        if (isCheckboxColumn) {
          // For checkbox columns: Convert both values to boolean for consistent comparison
          // This handles cases where values might be stored as strings ('true'/'false') or actual booleans
          const originalBooleanValue = originalValue === true || originalValue === 'true';
          const newBooleanValue = newValue === true || newValue === 'true';
          areValuesEqual = originalBooleanValue === newBooleanValue;
        } else {
          // For non-checkbox columns: Direct value comparison
          areValuesEqual = originalValue === newValue;
        }

        // If values match, remove from editedCells
        if (areValuesEqual) {
          setEditedCells((prev) => {
            const newSet = new Set(prev);
            newSet.delete(cellId);
            return newSet;
          });

          // Update the grid cell style
          if (gridApiRef.current) {
            gridApiRef.current.refreshCells({
              force: true,
              columns: [colId],
              rowNodes: [event.node],
            });
          }
        } else {
          // If the value is different from original, add to editedCells
          setEditedCells((prev) => new Set(prev).add(cellId));
        }

        handleAutoPopulate({
          event,
          configs: autoPopulateConfigs,
          alias,
        });
      },
      [autoPopulateConfigs, alias, originalValues, gridApiRef]
    );

    /**
     * Handles state updates after successful data save operations
     * Normalizes boolean values, updates grid state, and rebuilds reference values for change tracking
     *
     * @param updatedData - Array of updated row data or null if no changes
     */
    const handlePostSaveStateUpdate = useCallback(
      (updatedData?: Record<string, any>[] | null) => {
        if (updatedData) {
          // Convert string boolean values to actual booleans for grid display
          // This ensures proper checkbox behavior in the grid while maintaining data consistency
          const normalizedGridData = getNormalizedGridData(updatedData);
          const referenceValues = extractTrackableGridData(normalizedGridData);
          // Update grid state with normalized data
          setRowData(normalizedGridData);
          setEditedCells(new Set()); // Reset edited cells tracking

          // Build new reference values for future edit comparisons
          // Creates a map of cellId -> value for tracking changes
          setOriginalValues(referenceValues);
          alert('Changes saved successfully!');
        } else {
          alert('No changes to save');
        }
      },
      [setRowData, setEditedCells, setOriginalValues]
    );

    // Handles saving all changes made to the grid data
    // Clears the edited cells tracking after successful save
    // Displays success/error messages to the user
    const saveChanges = useCallback(async () => {
      try {
        if (!gridApiRef.current) {
          throw new Error('Grid API not initialized');
        }

        const updatedData = await handleSaveChanges(
          rowData,
          editedCells,
          controlAttributesData,
          controlId,
          originalValues // Pass originalValues here
        );

        // Only proceed if there were actual changes
        handlePostSaveStateUpdate(updatedData);
      } catch (error) {
        console.error('Error saving changes:', error);
        alert(error instanceof Error ? error.message : 'Failed to save changes. Please try again.');
      }
    }, [rowData, controlId, controlAttributesData, editedCells, originalValues]);

    // Initializes the grid when it's ready
    // Stores the grid API reference and resets the default filters flag
    // Shows initial loading overlay
    const onGridReady = useCallback(
      (params: GridReadyEvent) => {
        gridApiRef.current = params.api;

        // Set up server-side datasource for GL data only
        if (dataType === 'GL') {
          const datasource = {
            getRows: (serverParams: any) => {
              // Use existing processed data but with server-side pagination
              const processed = processControlData(controlData);
              const startRow = serverParams.startRow || 0;
              const endRow = serverParams.endRow || 300;
              const pageData = processed.slice(startRow, endRow);

              // Return with total count of 10000 (from backend)
              serverParams.success({
                rowData: pageData,
                rowCount: 10000,
              });
            },
          };

          (params.api as any).setServerSideDatasource(datasource);
        }

        if (Object.keys(columnState).length > 0) {
          const initialState: ColumnState[] = columnDefs
            .filter((col): col is ColDef & { field: string } => Boolean(col.field))
            .map((col) => ({
              colId: col.field,
              hide: col.hide ?? false,
            }));
          params.api.applyColumnState({
            state: initialState,
            applyOrder: true,
            defaultState: { hide: false },
          });
          initialColumnStateRef.current = initialState;
        }
        loadStartTime.current = performance.now();
      },
      [columnState, columnDefs, dataType, controlData]
    );

    // Updates the active filters state whenever filters change in the grid
    const onFilterChanged = useCallback(
      (event: FilterChangedEvent) => {
        const filterModel = event.api.getFilterModel();
        const filterModelWithDisplayNames = Object.entries(filterModel).reduce(
          (acc, [key, value]) => {
            const column = event.api.getColumnDef(key);
            if (value && Object.keys(value).length > 0) {
              return {
                ...acc,
                [key]: { ...value, displayName: column?.headerName || key },
              };
            }
            return acc;
          },
          {}
        );

        setFilteredRowCount(event.api.getDisplayedRowCount());
        setActiveFilters(filterModelWithDisplayNames);
        onFilterChange?.(filterModelWithDisplayNames);
      },
      [onFilterChange]
    );

    // Configure options passed on to the grid
    const gridOptions = useMemo(
      () => ({
        ...createGridOptions(defaultColDef, rowData, columnDefs, onCellValueChanged, onGridReady),
        sideBar: {
          toolPanels: [
            {
              id: 'columns',
              labelDefault: 'Columns',
              labelKey: 'columns',
              iconKey: 'columns',
              toolPanel: 'agColumnsToolPanel',
              toolPanelParams: {
                suppressRowGroups: !additionalDataEnabled, // Enable row groups for GL controls
                suppressValues: !additionalDataEnabled, // Enable values for GL controls
                suppressPivots: !additionalDataEnabled, // Enable pivots for GL controls
                suppressPivotMode: !additionalDataEnabled, // Enable pivot mode for GL controls
                suppressColumnFilter: false,
                suppressColumnSelectAll: false,
              },
            },
            {
              id: 'filters',
              labelDefault: 'Filters',
              labelKey: 'filters',
              iconKey: 'filter',
              toolPanel: 'agFiltersToolPanel',
              toolPanelParams: {
                suppressFilterSearch: false,
              },
            },
          ],
          defaultToolPanel: '',
          position: 'left', // explicitly type the position
        } as SideBarDef,
        loading: additionalDataEnabled ? isLoading && rowData.length > 0 : isLoading,
        loadingOverlayComponent: CustomLoadingOverlay,
        noRowsOverlayComponent: additionalDataEnabled ? 'customNoRowsOverlay' : undefined,
        overlayNoRowsTemplate: additionalDataEnabled
          ? `<div style="padding: 20px; text-align: center; color: #5f6b7a; font-size: 14px;">
            No rows loaded
          </div>`
          : undefined,
        rowBuffer: 100, // Number of rows to render outside visible area
        maxBlocksInCache: 10, // Maximum blocks to cache
        onFilterChanged: onFilterChanged,
        suppressColumnVirtualisation: true,
        maintainColumnOrder: true,
        suppressPropertyNamesCheck: true,
        suppressColumnStateEvents: false,
        enableRangeSelection: true,
        suppressRowClickSelection: true,
      }),
      [
        defaultColDef,
        rowData,
        columnDefs,
        onCellValueChanged,
        onGridReady,
        onFilterChanged,
        CustomCellPopoverRenderer,
        isLoading,
      ]
    );

    useImperativeHandle(
      ref,
      () => ({
        exportDataAsCsv: () => {
          if (gridApiRef.current) {
            gridApiRef.current.exportDataAsCsv();
          }
        },
        saveChanges: async () => {
          await saveChanges();
        },
        getColumnState: () => {
          return gridApiRef.current?.getColumnState() || [];
        },
        resetFilters: () => {
          if (gridApiRef.current) {
            gridApiRef.current.setFilterModel({});
            setActiveFilters({});
            gridApiRef.current.refreshCells({ force: true });
            gridApiRef.current.onFilterChanged();
          }
        },
        refreshData: (data: RecordAttributes[][]) => {
          const processed = processControlData(data);
          setRowData(processed);
          if (gridApiRef.current) {
            gridApiRef.current.setGridOption('rowData', processed);
            gridApiRef.current.refreshCells({ force: true });
            gridApiRef.current.redrawRows();
          }
        },
        applyColumnState: (state: ColumnState[]) => {
          if (gridApiRef.current) {
            gridApiRef.current.applyColumnState({
              state,
              applyOrder: true,
            });
          }
        },
        api: gridApiRef.current,
      }),
      [gridApiRef, saveChanges]
    );
    return (
      <div data-testid="control-summary-page">
        <Modal
          data-testid="modal"
          visible={showUnsavedChangesModal}
          onDismiss={handleUnsavedChangesCancel}
          header="Unsaved Changes"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={handleUnsavedChangesCancel}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleUnsavedChangesConfirm}>
                  Leave without saving
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          You have unsaved changes. Are you sure you want to leave this page?
        </Modal>
        <div style={{ width: '100%' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.5rem',
            }}
          ></div>
        </div>
        <div className="ag-theme-quartz">
          <AgGridReact
            {...gridOptions}
            rowSelection={rowSelection}
            onSelectionChanged={handleSelectionChanged}
          />
        </div>
        <DataSetDetailsModal
          visible={isModalVisible}
          onDismiss={() => {
            setIsModalVisible(false);
            setSelectedRowData(null);
            if (onDataRefresh) {
              onDataRefresh();
            }
          }}
          rowData={selectedRowData}
          attributesMetadata={controlAttributesData?.attributesMetadata}
          controlId={controlId}
          controlDatasetKey={controlAttributesData?.controlDatasetKey}
          previewScenarios={(controlAttributesData as any)?.previewScenarios}
          defaultFilters={controlAttributesData?.defaultFilters}
          onSave={async (updatedRow) => {
            // Create a new array with the updated row data
            const updatedRows = rowData.map((row) =>
              // safe assumption; controlDatasetKey will always be available
              row[controlAttributesData?.controlDatasetKey!] ===
              updatedRow[controlAttributesData?.controlDatasetKey!]
                ? updatedRow
                : row
            );
            setRowData(updatedRows);

            try {
              // Create a Set of edited cells for the updated row
              const editedCellsForRow = new Set(
                Object.keys(updatedRow)
                  .filter((key) => updatedRow[key] !== selectedRowData?.[key])
                  .map((key) => `${selectedRowData?.id}-${key}`)
              );
              const updatedData: Record<string, any>[] = await handleSaveChanges(
                updatedRows,
                editedCellsForRow,
                controlAttributesData,
                controlId,
                // This will always be available since user can only access this Modal by selecting a row
                selectedRowData!
              );
              handlePostSaveStateUpdate(updatedData);
            } catch (error) {
              // Revert grid to original data on save failure
              setRowData(rowData);
              throw error;
            }
          }}
        />
      </div>
    );
  }
);
